import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { BaseServices } from '../../../kuba.services';

type AppraisalTourStep = {
  selector: string;
  titleKey: string;
  bodyKey: string;
  tab?: string;
};

type AppraisalGuideRole = 'manager' | 'employee';

@Component({
  selector: 'appraisal-workflow-guide',
  templateUrl: './appraisal-workflow-guide.component.html',
  styleUrls: ['./appraisal-workflow-guide.component.scss'],
  standalone: false
})
export class AppraisalWorkflowGuideComponent implements OnInit, OnChanges {
  @Input() activeTab: string = 'list';
  @Input() isManager: boolean = false;
  @Input() canManageTasks: boolean = false;
  @Input() canManageTemplates: boolean = false;
  @Input() canViewRequests: boolean = false;
  @Input() hasAppraisalsForCurrentUser: boolean = false;
  @Input() storageRoleKey: string = 'employee';
  @Input() canSelectGuideRole: boolean = false;

  @Output() tabSelected = new EventEmitter<string>();

  dismissed = false;
  tourActive = false;
  currentTourStepIndex = 0;
  spotlightStyle: { [key: string]: string } = {};
  cardStyle: { [key: string]: string } = {};
  selectedGuideRole: AppraisalGuideRole = 'employee';

  private readonly hideKeyPrefix = 'appraisal-guide-hidden';
  private readonly tourSeenKeyPrefix = 'appraisal-guide-tour-seen';
  private readonly roleChoiceKeyPrefix = 'appraisal-guide-selected-role';

  ngOnInit(): void {
    this.initializeSelectedGuideRole();
    this.restoreHiddenState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isManager'] ||
      changes['canSelectGuideRole'] ||
      changes['storageRoleKey']
    ) {
      const previousRole = this.selectedGuideRole;
      this.initializeSelectedGuideRole();

      if (previousRole !== this.selectedGuideRole) {
        this.currentTourStepIndex = 0;
        this.closeTour();
        this.restoreHiddenState();
      }
    }

    if (this.tourActive) {
      setTimeout(() => this.updateTourLayout());
    }
  }

  get canShowGuideRoleSelector(): boolean {
    return this.canSelectGuideRole && this.isManager;
  }

  get isGuideManager(): boolean {
    return this.selectedGuideRole === 'manager';
  }

  get roleBadgeKey(): string {
    return this.isGuideManager
      ? 'APPRAISAL_GUIDE_ROLE_MANAGER'
      : 'APPRAISAL_GUIDE_ROLE_EMPLOYEE';
  }

  get bannerTitleKey(): string {
    return this.isGuideManager
      ? 'APPRAISAL_GUIDE_BANNER_TITLE'
      : 'APPRAISAL_GUIDE_BANNER_TITLE_EMPLOYEE';
  }

  get bannerBodyKey(): string {
    return this.isGuideManager
      ? 'APPRAISAL_GUIDE_BANNER_MANAGER'
      : 'APPRAISAL_GUIDE_BANNER_EMPLOYEE';
  }

  get tourSteps(): AppraisalTourStep[] {
    if (this.isGuideManager) {
      const steps: AppraisalTourStep[] = [];

      if (this.canManageTemplates) {
        steps.push({
          selector: '#appraisal-tab-settings',
          titleKey: 'APPRAISAL_TOUR_SETTINGS_TITLE',
          bodyKey: 'APPRAISAL_TOUR_SETTINGS_MANAGER_BODY',
          tab: 'settings'
        });
      }

      if (this.canViewRequests) {
        steps.push({
          selector: '#appraisal-tab-requests',
          titleKey: 'APPRAISAL_TOUR_REQUESTS_TITLE',
          bodyKey: 'APPRAISAL_TOUR_REQUESTS_MANAGER_BODY',
          tab: 'requests'
        });
      }

      if (this.canManageTemplates) {
        steps.push({
          selector: '#appraisal-tab-templates',
          titleKey: 'APPRAISAL_TOUR_TEMPLATES_TITLE',
          bodyKey: 'APPRAISAL_TOUR_TEMPLATES_MANAGER_BODY',
          tab: 'templates'
        });
      }

      steps.push({
        selector: '#appraisal-tab-list',
        titleKey: 'APPRAISAL_TOUR_LIST_TITLE',
        bodyKey: 'APPRAISAL_TOUR_LIST_MANAGER_BODY',
        tab: 'list'
      });

      if (this.hasAppraisalsForCurrentUser) {
        steps.push({
          selector: '#appraisal-tab-my-meetings',
          titleKey: 'APPRAISAL_TOUR_MY_MEETINGS_TITLE',
          bodyKey: 'APPRAISAL_TOUR_MY_MEETINGS_BODY',
          tab: 'my-meetings'
        });
      }

      if (this.canManageTasks) {
        steps.push({
          selector: '#appraisal-tab-tasks',
          titleKey: 'APPRAISAL_TOUR_TASKS_TITLE',
          bodyKey: 'APPRAISAL_TOUR_TASKS_MANAGER_BODY',
          tab: 'tasks'
        });
      }

      return steps;
    }

    const steps: AppraisalTourStep[] = [
      {
        selector: '#appraisal-module-title',
        titleKey: 'APPRAISAL_TOUR_OVERVIEW_EMPLOYEE_TITLE',
        bodyKey: 'APPRAISAL_TOUR_OVERVIEW_EMPLOYEE_BODY'
      }
    ];

    if (this.hasAppraisalsForCurrentUser) {
      steps.push({
        selector: this.isManager ? '#appraisal-tab-my-meetings' : '#appraisal-tab-list',
        titleKey: 'APPRAISAL_TOUR_LIST_EMPLOYEE_TITLE',
        bodyKey: 'APPRAISAL_TOUR_LIST_EMPLOYEE_BODY',
        tab: this.isManager ? 'my-meetings' : 'list'
      });
    }

    if (this.canViewRequests) {
      steps.push({
        selector: '#appraisal-tab-requests',
        titleKey: 'APPRAISAL_TOUR_REQUESTS_EMPLOYEE_TITLE',
        bodyKey: 'APPRAISAL_TOUR_REQUESTS_EMPLOYEE_BODY',
        tab: 'requests'
      });
    }

    return steps;
  }

  get currentTourStep(): AppraisalTourStep | null {
    return this.tourSteps[this.currentTourStepIndex] ?? null;
  }

  get isCurrentStepOpenable(): boolean {
    return !!this.currentTourStep?.tab;
  }

  get isCurrentStepActive(): boolean {
    return ( !!this.currentTourStep?.tab && this.currentTourStep.tab === this.activeTab
    );
  }

  get hasPreviousStep(): boolean {
    return this.currentTourStepIndex > 0;
  }

  get hasNextStep(): boolean {
    return this.currentTourStepIndex < this.tourSteps.length - 1;
  }

  get finishButtonKey(): string {
    return this.hasNextStep ? 'NEXT' : 'APPRAISAL_GUIDE_FINISH_TOUR';
  }

  startTour(): void {
    this.currentTourStepIndex = 0;
    this.tourActive = true;
    setTimeout(() => this.updateTourLayout());
  }

  selectGuideRole(role: AppraisalGuideRole): void {
    if (role === 'manager' && !this.isManager) {
      return;
    }

    if (!this.canSelectGuideRole && role !== this.defaultGuideRole) {
      return;
    }

    if (this.selectedGuideRole === role) {
      return;
    }

    this.selectedGuideRole = role;
    this.currentTourStepIndex = 0;
    this.closeTour();
    this.persistSelectedGuideRole();
    this.restoreHiddenState();
  }

  closeTour(): void {
    this.tourActive = false;
  }

  previousStep(): void {
    if (!this.hasPreviousStep) {
      return;
    }

    this.currentTourStepIndex -= 1;
    setTimeout(() => this.updateTourLayout());
  }

  nextStep(): void {
    if (!this.hasNextStep) {
      this.completeTour();
      return;
    }

    this.currentTourStepIndex += 1;
    setTimeout(() => this.updateTourLayout());
  }

  openCurrentStepTab(): void {
    if (!this.currentTourStep?.tab) {
      return;
    }

    this.tabSelected.emit(this.currentTourStep.tab);
    setTimeout(() => this.updateTourLayout());
  }

  dismissGuide(): void {
    this.dismissed = true;
    this.closeTour();

    try {
      localStorage.setItem(this.hideKey, '1');
    } catch {
      // Ignore localStorage failures and keep the in-memory state.
    }
  }

  showGuide(): void {
    this.dismissed = false;

    try {
      localStorage.removeItem(this.hideKey);
    } catch {
      // Ignore localStorage failures and keep the in-memory state.
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.tourActive) {
      this.updateTourLayout();
    }
  }

  private restoreHiddenState(): void {
    try {
      this.dismissed = localStorage.getItem(this.hideKey) === '1' || this.hasSeenTour();
    } catch {
      this.dismissed = false;
    }
  }

  private markTourSeen(): void {
    try {
      localStorage.setItem(this.tourSeenKey, '1');
    } catch {
      // Ignore localStorage failures and continue.
    }
  }

  private hasSeenTour(): boolean {
    try {
      return (
        localStorage.getItem(this.tourSeenKey) === '1' ||
        localStorage.getItem(this.legacyTourSeenKey) === '1'
      );
    } catch {
      return false;
    }
  }

  private completeTour(): void {
    this.tourActive = false;
    this.dismissed = true;
    this.markTourSeen();
  }

  private initializeSelectedGuideRole(): void {
    const defaultRole = this.defaultGuideRole;

    if (!this.canSelectGuideRole) {
      this.selectedGuideRole = defaultRole;
      return;
    }

    const storedRole = this.getStoredGuideRole();
    this.selectedGuideRole = storedRole || defaultRole;

    if (this.selectedGuideRole === 'manager' && !this.isManager) {
      this.selectedGuideRole = 'employee';
    }
  }

  private persistSelectedGuideRole(): void {
    try {
      localStorage.setItem(this.roleChoiceKey, this.selectedGuideRole);
    } catch {
      // Ignore localStorage failures and keep the in-memory state.
    }
  }

  private getStoredGuideRole(): AppraisalGuideRole | null {
    try {
      const storedRole = localStorage.getItem(this.roleChoiceKey);
      return storedRole === 'manager' || storedRole === 'employee'
        ? storedRole
        : null;
    } catch {
      return null;
    }
  }

  private updateTourLayout(): void {
    const step = this.currentTourStep;
    if (!step) {
      return;
    }

    const target = document.querySelector(step.selector) as HTMLElement | null;
    if (!target) {
      this.spotlightStyle = {};
      this.cardStyle = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 8;
    const spotlightTop = Math.max(rect.top - padding, 8);
    const spotlightLeft = Math.max(rect.left - padding, 8);
    const spotlightWidth = Math.min(
      rect.width + padding * 2,
      window.innerWidth - spotlightLeft - 8
    );
    const spotlightHeight = rect.height + padding * 2;

    this.spotlightStyle = {
      top: `${spotlightTop}px`,
      left: `${spotlightLeft}px`,
      width: `${spotlightWidth}px`,
      height: `${spotlightHeight}px`
    };

    const cardWidth = Math.min(360, window.innerWidth - 32);
    const estimatedCardHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > estimatedCardHeight + 24
        ? rect.bottom + 16
        : Math.max(16, rect.top - estimatedCardHeight - 16);
    const left = Math.min(
      Math.max(rect.left, 16),
      window.innerWidth - cardWidth - 16
    );

    this.cardStyle = {
      top: `${top}px`,
      left: `${left}px`,
      width: `${cardWidth}px`
    };
  }

  private get hideKey(): string {
    return `${this.hideKeyPrefix}:${BaseServices.BusinessId}:${BaseServices.UserId}:${this.effectiveRoleKey}`;
  }

  private get legacyTourSeenKey(): string {
    return `${this.tourSeenKeyPrefix}:${BaseServices.BusinessId}:${BaseServices.UserId}`;
  }

  private get tourSeenKey(): string {
    return `${this.tourSeenKeyPrefix}:${BaseServices.BusinessId}:${BaseServices.UserId}:${this.effectiveRoleKey}`;
  }

  private get roleChoiceKey(): string {
    return `${this.roleChoiceKeyPrefix}:${BaseServices.BusinessId}:${BaseServices.UserId}`;
  }

  private get effectiveRoleKey(): AppraisalGuideRole {
    return this.selectedGuideRole || this.defaultGuideRole;
  }

  private get defaultGuideRole(): AppraisalGuideRole {
    if (this.storageRoleKey === 'manager' || this.storageRoleKey === 'employee') {
      return this.storageRoleKey;
    }

    return this.isManager ? 'manager' : 'employee';
  }
}
